// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A machine's callouts, rendered and uploaded (M3-27, 07 §4).
//
// Separate from `soundBank.ts` because the two banks answer to different owners. A coil sound
// belongs to the *product* — every machine has bumpers and they all sound the same way — so
// `CUE_SPECS` is a constant. A callout belongs to the **machine**: NEON NOVA has ten, another
// machine will have twelve different ones, and neither list is knowable before a definition is
// loaded. Putting them in the cue bank would mean a global table that changes when a machine
// changes, which is a different thing wearing the same name.
//
// **Everything here is deferred** (M3-21's `firstUse`, arrived at from the other direction). A
// callout is over a second of five-formant filtering, ten of them is a good fraction of the
// whole render budget, and the earliest a machine can possibly say one is after a rule has
// fired. So none of them is rendered at load; `warmOne` fills them in a frame at a time, and
// `pick` renders on the spot for a callout wanted before the warm-up reaches it.

import { Rng } from '@core/rng/Rng'
import { noiseFrom } from './solenoids'
import { AUDIO_SEED } from './soundBank'
import type { MachineSpeechLine } from '@core/machine'
import { unknownPhonemes } from './phonemes'
import {
  calloutLengthMs,
  DEFAULT_PROFILE,
  renderCallout,
  VOICE_PROFILES,
  type SpeechLine,
  type VoiceProfile,
} from './speech'
import type { AudioBufferLike, AudioPlaybackContext } from './webAudio'

/** What a machine's `audio.voice` block asks for. */
export interface VoiceRequest {
  readonly profile?: string | undefined
  readonly pitch?: number | undefined
}

/** A callout, ready or waiting. */
interface Callout {
  readonly line: SpeechLine
  readonly lengthMs: number
  buffer: AudioBufferLike | undefined
}

export class SpeechBank {
  readonly profile: VoiceProfile
  readonly pitch: number
  /** Cost of everything rendered so far, milliseconds. All of it is after load, by design. */
  lateRenderMs = 0
  /**
   * What this machine says that nothing can pronounce, with the reason.
   *
   * Reported rather than skipped, exactly as `silentCues` reports a cue with no recipe. A
   * callout with a typo in one phoneme still loads, still fires, and says a slightly different
   * word; a callout naming a voice profile that does not exist speaks in the wrong voice. Both
   * are invisible from inside the game.
   */
  readonly problems: string[] = []

  private readonly context: AudioPlaybackContext
  private readonly callouts = new Map<string, Callout>()
  private readonly waiting: string[] = []
  private readonly now: () => number

  constructor(
    context: AudioPlaybackContext,
    lines: Readonly<Record<string, MachineSpeechLine>> | undefined,
    voice: VoiceRequest | undefined,
    now: () => number = () => 0,
  ) {
    this.context = context
    this.now = now

    const wanted = voice?.profile ?? DEFAULT_PROFILE
    const found = VOICE_PROFILES[wanted]
    if (found === undefined) {
      this.problems.push(`voice: no profile "${wanted}" — speaking as ${DEFAULT_PROFILE}`)
    }
    this.profile = found ?? VOICE_PROFILES[DEFAULT_PROFILE]!
    this.pitch = voice?.pitch ?? 1

    for (const id of Object.keys(lines ?? {}).sort()) {
      const raw = lines![id]!
      const line: SpeechLine = {
        text: raw.text,
        phonemes: raw.phonemes ?? '',
        emphasis: raw.emphasis ?? [],
      }
      if (line.phonemes.trim().length === 0) {
        // The format makes `phonemes` optional so a line can be captioned before it is voiced.
        // That is a legitimate authoring state and an illegitimate shipping one, so it is
        // reported rather than treated as a callout with nothing to say.
        this.problems.push(`${id}: captioned "${line.text}" with no phonemes`)
        continue
      }
      const bad = unknownPhonemes(line.phonemes)
      if (bad.length > 0) this.problems.push(`${id}: unknown phoneme ${bad.join(', ')}`)
      this.callouts.set(id, {
        line,
        lengthMs: calloutLengthMs(line, context.sampleRate),
        buffer: undefined,
      })
      this.waiting.push(id)
    }
  }

  get size(): number {
    return this.callouts.size
  }

  /** Callouts not rendered yet. Zero once the warm-up has caught up. */
  get pending(): number {
    return this.waiting.length
  }

  /** Render one pending callout, if there is one. Returns what it rendered. */
  warmOne(): string | undefined {
    const id = this.waiting[0]
    if (id === undefined) return undefined
    this.realise(id)
    return id
  }

  /** The buffer for a callout, rendering it now if the warm-up has not reached it. */
  pick(id: string): AudioBufferLike | undefined {
    const callout = this.callouts.get(id)
    if (callout === undefined) return undefined
    if (callout.buffer === undefined) this.realise(id)
    return callout.buffer
  }

  /** The caption NFR-022 requires, and how long it stays up. */
  captionOf(id: string): string | undefined {
    return this.callouts.get(id)?.line.text
  }

  lengthMsOf(id: string): number {
    return this.callouts.get(id)?.lengthMs ?? 0
  }

  get ids(): string[] {
    return [...this.callouts.keys()]
  }

  private realise(id: string): void {
    const at = this.waiting.indexOf(id)
    if (at >= 0) this.waiting.splice(at, 1)
    const callout = this.callouts.get(id)
    if (callout === undefined) return
    const started = this.now()
    // Its own stream, seeded from the callout's name, for the same reason every cue has one:
    // adding a callout must not change how the others sound. Jitter and shimmer are audible
    // enough that a shared stream would make every line after the new one a different take.
    const rng = new Rng(seedFor(id))
    const samples = renderCallout(
      callout.line,
      this.profile,
      this.pitch,
      this.context.sampleRate,
      noiseFrom(new Rng(seedFor(id) ^ 0x6e6f6973)),
      rng,
    )
    const buffer = this.context.createBuffer(1, samples.length, this.context.sampleRate)
    buffer.getChannelData(0).set(samples)
    callout.buffer = buffer
    this.lateRenderMs += this.now() - started
  }
}

/** FNV-1a over the callout's id, as `cueSeed` does over a cue's name. */
function seedFor(id: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 0x01000193)
  return (h ^ AUDIO_SEED) | 0
}
